import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { CustomerCardService } from '../../services/customer-card.service';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-add-card-callback',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, ProgressSpinnerModule],
  providers: [MessageService],
  templateUrl: './add-card-callback.html',
  styleUrl: './add-card-callback.css'
})
export class AddCardCallbackComponent implements OnInit {
  isProcessing = signal(true);
  isSuccess = signal(false);
  isError = signal(false);
  errorMessage = signal<string | null>(null);
  platformId = signal<string | null>(null);
  platformName = signal<string | null>(null);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cardService: CustomerCardService,
    private authService: AuthService,
    private settingsService: SettingsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin']);
      return;
    }

    // Get platformId from query params (environment comes from SettingsService)
    this.route.queryParams.subscribe(params => {
      if (params['platformId']) {
        this.platformId.set(params['platformId']);
      }
      
      // Get platform name from localStorage (stored when navigating from store)
      const storedPlatformName = localStorage.getItem('arkifihub_platformName');
      if (storedPlatformName) {
        this.platformName.set(storedPlatformName);
      }

      // Get transaction reference from URL (trxref or reference)
      const transactionRef = params['trxref'] || params['reference'];
      if (transactionRef && this.platformId()) {
        this.verifyAndSaveCard(transactionRef);
      } else {
        this.isProcessing.set(false);
        this.isError.set(true);
        this.errorMessage.set('Transaction reference not found');
      }
    });
  }

  async verifyAndSaveCard(transactionRef: string): Promise<void> {
    const platformId = this.platformId();
    if (!platformId) {
      this.isProcessing.set(false);
      this.isError.set(true);
      this.errorMessage.set('Platform ID is required');
      return;
    }

    try {
      // Get environment from SettingsService
      const environment = this.settingsService.getEnvironment();
      
      const result = await this.cardService.verifyAndSaveCard(
        platformId,
        environment,
        transactionRef
      );

      this.isProcessing.set(false);

      if (result.success && result.data) {
        this.isSuccess.set(true);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Card added successfully! Redirecting to your cards...',
          life: 3000
        });
        
        // Automatically redirect to cards page after 2 seconds
        setTimeout(() => {
          this.viewCards();
        }, 2000);
      } else {
        this.isError.set(true);
        this.errorMessage.set(result.message || 'Failed to verify card');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: result.message || 'Failed to verify card',
          life: 5000
        });
      }
    } catch (error: any) {
      this.isProcessing.set(false);
      this.isError.set(true);
      this.errorMessage.set(error.message || 'An error occurred');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'An error occurred',
        life: 5000
      });
      console.error('Error verifying card:', error);
    }
  }

  viewCards(): void {
    const platformId = this.platformId();
    if (platformId) {
      // Don't pass environment - it will be read from SettingsService
      this.router.navigate(['/cards'], { queryParams: { platformId } });
    } else {
      this.router.navigate(['/cards']);
    }
  }

  goBack(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    // Navigate back to store page if we have both platformId and platformName
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/store', platformId, encodedName]);
    } else {
      // Fallback to marketplace if we don't have store info
      this.router.navigate(['/marketplace']);
    }
  }
}
